// RaidMarkerIcon.tsx
//
// SVG recreations of the 8 WoW Classic/TBC raid target markers, drawn to read
// like the in-game icons (gem bevels + dark outlines) instead of flat unicode
// glyphs. Index order matches the game: 0=Star .. 7=Skull, so {rtN} = i + 1.
//
// RAID_MARKERS is the single source of truth for marker metadata; the glyph
// is still carried for plain-text contexts (textarea bodies, MRT export).

import React from "react";

export const RAID_MARKERS = [
  { i: 0, name: "Star",     color: "#FFE043", glyph: "★" },
  { i: 1, name: "Circle",   color: "#FF8A1E", glyph: "●" },
  { i: 2, name: "Diamond",  color: "#C341F0", glyph: "◆" },
  { i: 3, name: "Triangle", color: "#39E600", glyph: "▲" },
  { i: 4, name: "Moon",     color: "#9FD6FF", glyph: "☾" },
  { i: 5, name: "Square",   color: "#39C6FF", glyph: "■" },
  { i: 6, name: "Cross",    color: "#FF3A2E", glyph: "✖" },
  { i: 7, name: "Skull",    color: "#E8E8E8", glyph: "☠" },
];

export const raidMarkerOf = (i: number | null | undefined) =>
  i == null ? null : RAID_MARKERS.find((m) => m.i === i) || null;

const CIRCLE_DOTS = Array.from({ length: 8 }, (_, k) => {
  const a = (k * Math.PI) / 4;
  return { x: 50 + 41 * Math.cos(a), y: 50 + 41 * Math.sin(a) };
});

export function RaidMarkerIcon({
  marker,
  size = 18,
  className,
  title,
  style,
}: {
  marker: number | null | undefined;
  size?: number;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
}) {
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const meta = raidMarkerOf(marker);
  if (!meta) return null;

  const svgProps = {
    width: size,
    height: size,
    viewBox: "0 0 100 100",
    className,
    style,
    "aria-label": title || meta.name,
    role: "img" as const,
  };
  const label = <title>{title || meta.name}</title>;

  switch (meta.i) {
    case 0: // Star — gold 4-point star over a smaller rotated one
      return (
        <svg {...svgProps}>
          {label}
          <defs>
            <radialGradient id={`${uid}g`} cx="50%" cy="40%" r="62%">
              <stop offset="0%" stopColor="#FFF7C2" />
              <stop offset="45%" stopColor="#FFD84D" />
              <stop offset="100%" stopColor="#C8800A" />
            </radialGradient>
          </defs>
          <path
            d="M50 20 L58 42 L80 50 L58 58 L50 80 L42 58 L20 50 L42 42 Z"
            transform="rotate(45 50 50)"
            fill="#DE9B14" stroke="#7A4A00" strokeWidth="3" strokeLinejoin="round"
          />
          <path
            d="M50 3 L59.5 40.5 L97 50 L59.5 59.5 L50 97 L40.5 59.5 L3 50 L40.5 40.5 Z"
            fill={`url(#${uid}g)`} stroke="#7A4A00" strokeWidth="3" strokeLinejoin="round"
          />
        </svg>
      );
    case 1: // Circle — orange coin with riveted rim
      return (
        <svg {...svgProps}>
          {label}
          <defs>
            <radialGradient id={`${uid}g`} cx="38%" cy="32%" r="75%">
              <stop offset="0%" stopColor="#FFD9A0" />
              <stop offset="45%" stopColor="#FB923C" />
              <stop offset="100%" stopColor="#C2570A" />
            </radialGradient>
          </defs>
          <circle cx="50" cy="50" r="46" fill="#8A4207" stroke="#5F2D02" strokeWidth="3" />
          <circle cx="50" cy="50" r="35" fill={`url(#${uid}g)`} stroke="#5F2D02" strokeWidth="2" />
          {CIRCLE_DOTS.map((d, k) => (
            <circle key={k} cx={d.x} cy={d.y} r="3.1" fill="#5F2D02" opacity="0.85" />
          ))}
        </svg>
      );
    case 2: // Diamond — purple gem
      return (
        <svg {...svgProps}>
          {label}
          <defs>
            <linearGradient id={`${uid}g`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#EFC7FF" />
              <stop offset="50%" stopColor="#A855F7" />
              <stop offset="100%" stopColor="#66159E" />
            </linearGradient>
          </defs>
          <path d="M50 3 L92 50 L50 97 L8 50 Z"
            fill={`url(#${uid}g)`} stroke="#43106B" strokeWidth="3" strokeLinejoin="round" />
          <path d="M50 16 L79 50 L50 84 L21 50 Z" fill="#FFFFFF" opacity="0.14" />
        </svg>
      );
    case 3: // Triangle — green, pointing up like the in-game marker
      return (
        <svg {...svgProps}>
          {label}
          <defs>
            <linearGradient id={`${uid}g`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#CFF7B8" />
              <stop offset="55%" stopColor="#46CC1F" />
              <stop offset="100%" stopColor="#17660B" />
            </linearGradient>
          </defs>
          <path d="M50 7 L94 89 L6 89 Z"
            fill={`url(#${uid}g)`} stroke="#0F4D06" strokeWidth="3" strokeLinejoin="round" />
          <path d="M50 27 L78 81 L22 81 Z" fill="#FFFFFF" opacity="0.10" />
        </svg>
      );
    case 4: // Moon — pale silver crescent
      return (
        <svg {...svgProps}>
          {label}
          <defs>
            <linearGradient id={`${uid}g`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="50%" stopColor="#C7D7E8" />
              <stop offset="100%" stopColor="#8CA4BE" />
            </linearGradient>
          </defs>
          <path d="M 71 8 A 45 45 0 1 0 92 60 A 38 38 0 0 1 71 8 Z"
            fill={`url(#${uid}g)`} stroke="#44586F" strokeWidth="3" strokeLinejoin="round" />
        </svg>
      );
    case 5: // Square — blue beveled plate
      return (
        <svg {...svgProps}>
          {label}
          <defs>
            <linearGradient id={`${uid}g`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#9CC8FF" />
              <stop offset="55%" stopColor="#2E7FE6" />
              <stop offset="100%" stopColor="#123F8C" />
            </linearGradient>
          </defs>
          <rect x="8" y="8" width="84" height="84" rx="10"
            fill={`url(#${uid}g)`} stroke="#0B2A66" strokeWidth="3" />
          <rect x="21" y="21" width="58" height="58" rx="6"
            fill="none" stroke="#FFFFFF" strokeOpacity="0.25" strokeWidth="3" />
          {[[27, 27], [73, 27], [27, 73], [73, 73]].map(([x, y], k) => (
            <circle key={k} cx={x} cy={y} r="3.4" fill="#0B2A66" opacity="0.7" />
          ))}
        </svg>
      );
    case 6: // Cross — red X
      return (
        <svg {...svgProps}>
          {label}
          <defs>
            <linearGradient id={`${uid}g`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF9A8C" />
              <stop offset="55%" stopColor="#E6392B" />
              <stop offset="100%" stopColor="#8F1004" />
            </linearGradient>
          </defs>
          <path d="M50 33 L73 10 L90 27 L67 50 L90 73 L73 90 L50 67 L27 90 L10 73 L33 50 L10 27 L27 10 Z"
            fill={`url(#${uid}g)`} stroke="#5E0B03" strokeWidth="3" strokeLinejoin="round" />
        </svg>
      );
    case 7: // Skull — white skull
      return (
        <svg {...svgProps}>
          {label}
          <defs>
            <linearGradient id={`${uid}g`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="55%" stopColor="#DDE2E8" />
              <stop offset="100%" stopColor="#A7B0BA" />
            </linearGradient>
          </defs>
          <path d="M50 6 C27 6 12 22 12 43 C12 55 18 64 27 69 L27 80 C27 87 32 92 39 92 L61 92 C68 92 73 87 73 80 L73 69 C82 64 88 55 88 43 C88 22 73 6 50 6 Z"
            fill={`url(#${uid}g)`} stroke="#3A4048" strokeWidth="3" />
          <ellipse cx="35" cy="45" rx="8.5" ry="10.5" fill="#23262B" />
          <ellipse cx="65" cy="45" rx="8.5" ry="10.5" fill="#23262B" />
          <path d="M50 56 L44.5 67 L55.5 67 Z" fill="#23262B" />
          <path d="M42 91 L42 78 M50 91 L50 76 M58 91 L58 78"
            stroke="#3A4048" strokeWidth="3" strokeLinecap="round" fill="none" />
        </svg>
      );
    default:
      return null;
  }
}

export default RaidMarkerIcon;
